import { useMemo } from "react";
import { useBlogPosts, type BlogPost } from "./useBlogPosts";

// Helper to check array containment
const arrayContains = (arr: string[] | undefined | null, val: string) => {
  if (!arr) return false;
  return arr.includes(val);
};

export function useRelatedArticles(treatmentId: string, treatmentCategory: string, limit = 6) {
  const { data: allPosts, isLoading } = useBlogPosts();

  const relatedPosts = useMemo(() => {
    if (!allPosts) return [];
    
    // 1. Identify Specific Articles (Direct or Category related)
    const specificRelated: BlogPost[] = [];
    
    // We'll use a Set to track IDs to prevent duplicates and efficiently exclude strictly specific ones from tourism fallback
    const seenIds = new Set<string>();

    allPosts.forEach(post => {
      let isRelated = false;

      // Direct Relation (Priority 1)
      if (post.related_treatment_ids && arrayContains(post.related_treatment_ids, treatmentId)) {
        isRelated = true;
      }
      // Category Relation (Priority 2)
      else if (post.related_treatment_categories && arrayContains(post.related_treatment_categories, treatmentCategory)) {
        isRelated = true;
      }

      if (isRelated) {
        specificRelated.push(post);
        seenIds.add(post.id);
      }
    });

    // 2. Take up to 5 Specific Articles
    const topSpecific = specificRelated.slice(0, 5);
    
    // Re-sync seenIds to only include those in the top 5 (so we don't exclude valid tourism articles if they were specific but dropped? 
    // Actually, if a tourism article was "specific" (e.g. tagged with cardiac), it would be in specificRelated.
    // If it was dropped from top 5, we might want to pick it up again if it matches "Medical Tourism"?
    // Unlikely a specific article is ALSO "Medical Tourism" category unless generic. 
    // Let's just track what we HAVE picked.
    const pickedIds = new Set(topSpecific.map(p => p.id));

    // 3. Find Medical Tourism Articles for filler
    const tourismArticles = allPosts.filter(post => 
      !pickedIds.has(post.id) && 
      (post.category === "Medical Tourism" || post.category === "Medical tourism")
    );

    // 4. Combine: Top 5 Specific + Medical Tourism
    const combined = [...topSpecific, ...tourismArticles];

    // 5. Slice to final limit
    return combined.slice(0, limit);

  }, [allPosts, treatmentId, treatmentCategory, limit]);

  return {
    data: relatedPosts,
    isLoading,
  };
}

